/**
 * Quick Cart Upsell — AJAX add to cart
 * Save as assets/quick-cart-upsell.js
 *
 * How it refreshes the drawer:
 * This theme's quick-cart section doesn't expose a documented JS API,
 * so rather than guessing at internal events, this uses Shopify's
 * built-in Section Rendering API (/?section_id=...) to re-fetch the
 * quick-cart section's HTML straight from the server after adding a
 * product.
 *
 * IMPORTANT — targeted refresh, not a full section swap:
 * Earlier versions of this script replaced the ENTIRE quick-cart
 * section wholesale. That destroyed the drawer's outer shell (header,
 * close button, overlay) and, with it, any click listeners the theme
 * had already bound to those specific elements — breaking the close
 * button, overlay-click-to-close, and eventually even the cart-icon
 * trigger. To avoid that entirely, this version only swaps the INNER
 * content that actually changes after adding a product: the items
 * list (.quick-cart__items, which includes the upsell list) and the
 * footer (.quick-cart__footer, which has the subtotal). The header,
 * close button, and overlay are never touched, so their existing
 * event bindings stay intact.
 *
 * Quantity/remove buttons inside quick-cart__item rely on this
 * theme's existing data-input-item / data-remove-item delegation —
 * if those stop responding after a refresh, the theme is likely
 * binding listeners directly to elements rather than delegating from
 * a parent, and would need a small tweak from whoever maintains the
 * theme JS.
 */
(function () {
  'use strict';

  function getSectionRoot(el) {
    return el.closest('[data-section-type="quick-cart"]');
  }

  function getVariantId(card) {
    var select = card.querySelector('[data-quick-cart-upsell-variant-select]');
    if (select) return select.value;
    var hidden = card.querySelector('[data-quick-cart-upsell-variant-id]');
    return hidden ? hidden.value : null;
  }

  function setLoading(button, isLoading) {
    button.disabled = isLoading;
    button.classList.toggle('is-loading', isLoading);
    var spinner = button.querySelector('.quick-cart__upsell-item-spinner');
    if (spinner) spinner.hidden = !isLoading;
  }

  // Swaps only the inner content of a matching element, never the
  // element itself — so any listeners bound to the element (or its
  // ancestors) are preserved.
  function swapInnerContent(root, newDoc, selector) {
    var current = root.querySelector(selector);
    var incoming = newDoc.querySelector(selector);
    if (current && incoming) {
      current.innerHTML = incoming.innerHTML;
      return true;
    }
    return false;
  }

  function refreshQuickCartSection(root) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();

    return fetch('/?section_id=' + encodeURIComponent(sectionId))
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newRoot = doc.querySelector('[data-section-type="quick-cart"]');
        if (!newRoot) return;

        // Only refresh the parts that actually change. Header,
        // close button, and overlay are left completely alone.
        swapInnerContent(root, newRoot, '.quick-cart__items');
        swapInnerContent(root, newRoot, '.quick-cart__footer');

        // Cart item count badge in the header (e.g. the <sup> next
        // to "Cart") also needs updating — swap just that span/heading
        // if present, without touching the rest of the header.
        var currentHeading = root.querySelector('.quick-cart__heading');
        var incomingHeading = newRoot.querySelector('.quick-cart__heading');
        if (currentHeading && incomingHeading) {
          currentHeading.innerHTML = incomingHeading.innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent('quick-cart:refreshed', { bubbles: true })
        );
      });
  }

  function addToCart(button) {
    var card = button.closest('[data-quick-cart-upsell-item]');
    var root = getSectionRoot(button);
    if (!card || !root) return;

    var variantId = getVariantId(card);
    if (!variantId) return;

    setLoading(button, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        return refreshQuickCartSection(root);
      })
      .catch(function (err) {
        console.error('[quick-cart-upsell]', err);
        button.classList.add('quick-cart__upsell-item-add--error');
        setTimeout(function () {
          button.classList.remove('quick-cart__upsell-item-add--error');
        }, 1500);
        setLoading(button, false);
      });
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-quick-cart-upsell-add]');
    if (!button) return;
    e.preventDefault();
    addToCart(button);
  });
})();